import { useState } from "react";
import { EnvelopeSimple, Key, Clock, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { PasswordFields, passwordProblem } from "../PasswordFields";

export const LOGIN_MODES = [
  ["invite", EnvelopeSimple, "E-mail them a link", "They set their own password · welcome pack attached"],
  ["password", Key, "Set a password now", "You hand the password over · pack e-mailed"],
  ["later", Clock, "No login yet", "Add one later from the reseller page"],
];

/* Radio-style picker + password fields, shared by the reseller create dialog. */
export function ResellerLoginChoice({ value, onChange, hasEmail }) {
  const { mode, password, forceChange } = value;
  return (
    <div className="space-y-3" data-testid="reseller-login-choice">
      <Label className="text-xs">Portal login</Label>
      <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="How the reseller gets access">
        {LOGIN_MODES.map(([k, Icon, label, sub]) => {
          const disabled = k !== "later" && !hasEmail;
          return (
            <button key={k} type="button" role="radio" aria-checked={mode === k} disabled={disabled} onClick={() => onChange({ ...value, mode: k })} data-testid={`reseller-login-mode-${k}`}
              className={`text-left rounded-lg border px-3 py-2 transition-colors disabled:opacity-50 ${mode === k ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"}`}>
              <span className="flex items-center gap-1.5 text-sm font-medium"><Icon size={14} /> {label}</span>
              <span className="block text-[11px] text-muted-foreground">{disabled ? "Needs an e-mail address" : sub}</span>
            </button>
          );
        })}
      </div>
      {mode === "password" && <PasswordFields value={password} onChange={(p) => onChange({ ...value, password: p })} forceChange={forceChange} onForceChange={(f) => onChange({ ...value, forceChange: f })} idPrefix="reseller-pw" />}
    </div>
  );
}

export const loginChoiceProblem = (value, hasEmail) => {
  if (value.mode === "later") return null;
  if (!hasEmail) return "Add an e-mail address or choose 'No login yet'";
  if (value.mode === "password") return !value.password ? "Enter a password" : passwordProblem(value.password);
  return null;
};

/* What the admin sees once the reseller + login exist: the invite link or the password to hand over. */
export function ResellerLoginResult({ result, password, onDone }) {
  const [copied, setCopied] = useState("");
  const copy = async (text, key) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(""), 1500); } catch { toast.error("Couldn't copy"); }
  };
  return (
    <div className="space-y-3" data-testid="reseller-login-result">
      <p className="text-sm">Reseller <b>{result.name}</b> created with code prefix <b className="font-mono">{result.handle}</b>.</p>
      {result.login?.mode === "invite" && (
        <>
          <p className="text-sm text-muted-foreground">
            {result.login.email_sent ? <>Invite + welcome pack e-mailed to <b>{result.login.email}</b>. The link below is the same one, in case they can't find it.</>
              : <>E-mail couldn't be sent{result.login.email_error ? ` (${result.login.email_error})` : ""} — share this link with <b>{result.login.email}</b> yourself.</>}
          </p>
          <Label className="text-xs">Set-password link (valid 14 days)</Label>
          <div className="flex gap-2">
            <Input value={result.login.invite_url} readOnly className="font-mono text-xs" data-testid="reseller-login-invite-link" />
            <Button type="button" variant="outline" size="sm" onClick={() => copy(result.login.invite_url, "link")} data-testid="reseller-login-copy-link"><Copy size={14} /> {copied === "link" ? "Copied" : ""}</Button>
          </div>
        </>
      )}
      {result.login?.mode === "password" && (
        <>
          <p className="text-sm text-muted-foreground">Login created for <b>{result.login.email}</b>{result.login.pack_sent ? " and the welcome pack has been e-mailed" : ""}. The password is not e-mailed — hand it over directly.</p>
          <Label className="text-xs">Password you set</Label>
          <div className="flex gap-2">
            <Input value={password} readOnly className="font-mono text-xs" data-testid="reseller-login-password" />
            <Button type="button" variant="outline" size="sm" onClick={() => copy(password, "pw")} data-testid="reseller-login-copy-password"><Copy size={14} /> {copied === "pw" ? "Copied" : ""}</Button>
          </div>
          {result.login.force_change && <p className="text-xs text-muted-foreground">They'll be asked to choose their own password at first sign-in.</p>}
        </>
      )}
      {!result.login && <p className="text-sm text-muted-foreground">No portal login yet — add one any time from the <b>Portal login</b> tab on their page.</p>}
      <div className="flex justify-end"><Button onClick={onDone} data-testid="reseller-login-done">Done</Button></div>
    </div>
  );
}
